import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { organizationSchema, createFAQSchema } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Check, X, Star, Zap, Palette, Image } from "lucide-react";
import { Link } from "react-router-dom";

const FluxVsLeonardoAI = () => {
  const faqs = [
    {
      question: "What is Leonardo AI best for?",
      answer: "Leonardo AI excels at game assets, character design, and fantasy art. It has specialized models for different art styles and offers fine-tuning capabilities for consistent character generation."
    },
    {
      question: "Is FLUX better than Leonardo AI for product photos?",
      answer: "Yes, FLUX is better optimized for photorealistic product photography and bulk commercial use. Leonardo AI is more suited for creative, artistic, and gaming-related imagery."
    },
    {
      question: "Which is faster for bulk generation?",
      answer: "FLUX is significantly faster for bulk generation, capable of 1000+ images per hour. Leonardo AI is optimized for quality over speed, making it better for individual artistic pieces."
    },
    {
      question: "Does Leonardo AI have a free tier?",
      answer: "Yes, Leonardo AI offers 150 free tokens daily (approximately 30-50 images depending on settings). FLUX offers 5 free images daily with full resolution and commercial rights."
    },
    {
      question: "Which should I choose for game development?",
      answer: "Leonardo AI is the better choice for game assets due to its specialized models for characters, environments, and items. FLUX is better for marketing materials and product shots for your game."
    }
  ];

  const comparison = [
    { feature: "Photorealism", flux: "Excellent", leonardo: "Good", winner: "flux" },
    { feature: "Game Assets", flux: "Good", leonardo: "Excellent", winner: "leonardo" },
    { feature: "Bulk Generation", flux: "1000+/hour", leonardo: "Limited", winner: "flux" },
    { feature: "Character Consistency", flux: "Good", leonardo: "Excellent", winner: "leonardo" },
    { feature: "E-commerce Use", flux: "Excellent", leonardo: "Basic", winner: "flux" },
    { feature: "Art Styles", flux: "Good", leonardo: "Excellent", winner: "leonardo" },
    { feature: "Speed", flux: "Fast", leonardo: "Moderate", winner: "flux" },
    { feature: "Free Tier", flux: "5/day", leonardo: "150 tokens/day", winner: "leonardo" },
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="FLUX vs Leonardo AI 2026 | AI Image Generator Comparison"
        description="FLUX vs Leonardo AI comparison. Which AI is better for your creative needs? Complete analysis of features, game assets, photorealism & pricing for 2026."
        keywords="flux vs leonardo ai, leonardo ai comparison, ai game assets, flux ai vs leonardo, character generation ai, ai art comparison, game art generator ai"
        url="/flux-vs-leonardo-ai"
        jsonLd={[
          organizationSchema,
          createFAQSchema(faqs)
        ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'FLUX vs Leonardo AI', url: '/flux-vs-leonardo-ai' }
        ]}
      />
      
      <Header />
      
      <main className="w-full">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                Updated January 2026
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                FLUX vs Leonardo AI
                <span className="block text-primary mt-2">Complete 2026 Comparison</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                FLUX leads in photorealism and bulk generation. Leonardo AI excels at game assets and artistic styles. 
                Find your perfect match.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Verdict */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
              <div className="bg-card border rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold mb-2">Choose FLUX If:</h3>
                <ul className="text-left space-y-2 mt-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Product photography matters</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>You need bulk generation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>E-commerce is your focus</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Speed and volume are priorities</span>
                  </li>
                </ul>
                <Button asChild className="mt-6">
                  <Link to="/try-generator">Try FLUX Free</Link>
                </Button>
              </div>
              
              <div className="bg-card border rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold mb-2">Choose Leonardo AI If:</h3>
                <ul className="text-left space-y-2 mt-4">
                  <li className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-500" />
                    <span>Creating game assets</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-500" />
                    <span>Character design consistency</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-500" />
                    <span>Fantasy/artistic styles</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-500" />
                    <span>Fine-tuning custom models</span>
                  </li>
                </ul>
                <Button variant="outline" className="mt-6" disabled>
                  Visit Leonardo.ai
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Feature Comparison</h2>
            
            <div className="max-w-4xl mx-auto overflow-x-auto">
              <table className="w-full bg-card border rounded-xl">
                <thead>
                  <tr className="border-b">
                    <th className="p-4 text-left">Feature</th>
                    <th className="p-4 text-center">FLUX</th>
                    <th className="p-4 text-center">Leonardo AI</th>
                    <th className="p-4 text-center">Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-4 font-medium">{row.feature}</td>
                      <td className={`p-4 text-center ${row.winner === 'flux' ? 'text-green-600 font-semibold' : ''}`}>
                        {row.flux}
                      </td>
                      <td className={`p-4 text-center ${row.winner === 'leonardo' ? 'text-purple-600 font-semibold' : ''}`}>
                        {row.leonardo}
                      </td>
                      <td className="p-4 text-center">
                        {row.winner === 'flux' && <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">FLUX</span>}
                        {row.winner === 'leonardo' && <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-sm">Leonardo</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card border rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Try FLUX?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Experience FLUX's photorealistic quality and bulk generation power. Start free today.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/try-generator">Try FLUX Free Now</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FluxVsLeonardoAI;
